import React, { useMemo, useState } from 'react';

function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  labelKey = 'label',
  valueKey = 'value',
  disabled = false,
  clearable = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = React.useRef();
  const dropdownRef = React.useRef();

  const selectedOption = useMemo(
    () => options.find((o) => String(o[valueKey]) === String(value)),
    [options, value, valueKey]
  );

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((opt) =>
      String(opt[labelKey]).toLowerCase().includes(normalized) ||
      String(opt[valueKey]).toLowerCase().includes(normalized)
    );
  }, [options, query, labelKey, valueKey]);

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        inputRef.current && !inputRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative w-full">
      <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={open ? query : (selectedOption ? selectedOption[labelKey] : '')}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => !disabled && setOpen(true)}
          disabled={disabled}
          className="flex-1 outline-none"
        />
        {clearable && selectedOption && !disabled && (
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700"
            onClick={(e) => { e.stopPropagation(); onChange(''); setQuery(''); setOpen(false); inputRef.current?.focus(); }}
            tabIndex={-1}
          >
            ✕
          </button>
        )}
        <button
          type="button"
          className="text-gray-400 hover:text-gray-700 focus:outline-none"
          onClick={() => { if (!disabled) setOpen((o) => !o); inputRef.current?.focus(); }}
          tabIndex={-1}
          aria-label="Toggle dropdown"
        >
          ▾
        </button>
      </div>
      {open && !disabled && (
        <ul
          ref={dropdownRef}
          className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-400">No options found</li>
          ) : (
            filtered.map((opt) => (
              <li
                key={opt[valueKey]}
                className="cursor-pointer px-3 py-2 hover:bg-indigo-100"
                onMouseDown={(e) => { e.preventDefault(); onChange(String(opt[valueKey])); setOpen(false); setQuery(''); inputRef.current?.blur(); }}
              >
                {opt[labelKey]}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export default function QuestionFilters({
  qaSearch,
  setQaSearch,
  tests,
  qaTestFilter,
  setQaTestFilter,
  subjects,
  qaSubjectFilter,
  setQaSubjectFilter,
  availableTopics,
  qaTopicFilter,
  setQaTopicFilter,
  qaDifficultyFilter,
  setQaDifficultyFilter,
  activeTest,
  onAdd
}) {
  // Helper for filter chips
  const filterChips = [
    qaTestFilter && (tests || []).find(t => String(t.id) === String(qaTestFilter))?.title && {
      label: (tests || []).find(t => String(t.id) === String(qaTestFilter))?.title,
      onRemove: () => setQaTestFilter(''),
    },
    qaSubjectFilter && (subjects || []).find(s => String(s.id) === String(qaSubjectFilter))?.name && {
      label: (subjects || []).find(s => String(s.id) === String(qaSubjectFilter))?.name,
      onRemove: () => setQaSubjectFilter(''),
    },
    qaTopicFilter && (availableTopics || []).find(t => String(t.id) === String(qaTopicFilter))?.name && {
      label: (availableTopics || []).find(t => String(t.id) === String(qaTopicFilter))?.name,
      onRemove: () => setQaTopicFilter(''),
    },
    qaDifficultyFilter && {
      label: qaDifficultyFilter,
      onRemove: () => setQaDifficultyFilter(''),
    },
    qaSearch && {
      label: `"${qaSearch}"`,
      onRemove: () => setQaSearch(''),
    },
  ].filter(Boolean);

  const hasFilters = filterChips.length > 0;

  return (
    <div className="sticky top-0 z-10 bg-gradient-to-b from-indigo-50 via-white to-white border border-gray-200 rounded-lg p-4 shadow-sm mb-4 transition-all">
      <div className="flex flex-col md:flex-row justify-between items-start gap-2 mb-4">
        <div>
          <h3 className="text-2xl font-bold text-indigo-600">Question Bank</h3>
          <p className="text-sm text-gray-500">Filter and manage question content by test, subject, topic and difficulty.</p>
          {activeTest && (
            <p className="text-sm text-gray-600 mt-1">Showing questions for test: <span className="font-semibold text-indigo-700">{activeTest.title}</span></p>
          )}
        </div>
        <button
          className="mt-2 md:mt-0 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-emerald-300 transition"
          onClick={onAdd}
          aria-label="Add new question"
        >
          + Add Question
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 items-center">
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Search questions..."
            value={qaSearch}
            onChange={e => setQaSearch(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 w-full md:w-auto focus:ring-2 focus:ring-indigo-150 pr-8 transition"
            aria-label="Search questions"
          />
          {qaSearch && (
            <button
              type="button"
              className="absolute right-2 text-gray-400 hover:text-gray-700 focus:outline-none"
              onClick={() => setQaSearch('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <SearchableSelect
          options={(tests || []).map((t) => ({ value: t.id, label: t.title }))}
          value={qaTestFilter}
          onChange={(v) => setQaTestFilter(v)}
          placeholder="All tests"
          aria-label="Filter by test"
        />

        <SearchableSelect
          options={(subjects || []).map((s) => ({ value: s.id, label: s.name }))}
          value={qaSubjectFilter}
          onChange={(v) => setQaSubjectFilter(v)}
          placeholder="All subjects"
          aria-label="Filter by subject"
        />

        <SearchableSelect
          options={(availableTopics || []).map((t) => ({ value: t.id, label: t.name }))}
          value={qaTopicFilter}
          onChange={(v) => setQaTopicFilter(v)}
          placeholder="All topics"
          disabled={!qaSubjectFilter}
          aria-label="Filter by topic"
        />

        <SearchableSelect
          options={[
            { value: 'Easy', label: 'Easy' },
            { value: 'Medium', label: 'Medium' },
            { value: 'Hard', label: 'Hard' },
            { value: 'Very Hard', label: 'Very Hard' },
          ]}
          value={qaDifficultyFilter}
          onChange={(v) => setQaDifficultyFilter(v)}
          placeholder="Any difficulty"
          aria-label="Filter by difficulty"
        />
      </div>

      {hasFilters && (
        <div className="flex flex-wrap gap-2 mt-3 items-center animate-fade-in" aria-label="Active filters">
          <span className="text-xs text-gray-500 mr-2">{filterChips.length} filter{filterChips.length > 1 ? 's' : ''} active</span>
          {filterChips.map((chip, idx) => (
            <span
              key={idx}
              className="inline-flex items-center bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-3 py-1 text-xs font-medium shadow-sm transition-all focus-within:ring-2 focus-within:ring-indigo-300"
              tabIndex={0}
              onKeyDown={e => (e.key === 'Backspace' || e.key === 'Delete') && chip.onRemove()}
            >
              {chip.label}
              <button
                type="button"
                className="ml-2 text-indigo-400 hover:text-indigo-700 focus:outline-none"
                onClick={chip.onRemove}
                aria-label={`Remove filter ${chip.label}`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            className="ml-2 text-xs text-gray-500 hover:text-red-600 border border-transparent rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-200 transition"
            onClick={() => {
              setQaTestFilter('');
              setQaSubjectFilter('');
              setQaTopicFilter('');
              setQaDifficultyFilter('');
              setQaSearch('');
            }}
            aria-label="Clear all filters"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="mt-4 border-t border-gray-100" />
    </div>
  );
}

export { SearchableSelect };
